import * as Sentry from "@sentry/react-native";
import { observer } from "mobx-react";
import React, { useCallback, useState } from "react";
import { FormattedMessage } from "react-intl";
import { Image, StyleSheet } from "react-native";
import { z } from "zod";
import { Modal } from "~/components/Modal";
import { Spacer } from "~/components/Spacer";
import { Pressable } from "~/components/V2/Pressable";
import { SpinnerV2 } from "~/components/V2/SpinnerV2";
import { BodyText } from "~/components/V2/Text/BodyText";
import { View } from "~/components/V2/View";
import { useErrorAlertV2 } from "~/components/V2/error-alert-v2";
import type { UserInstance } from "~/mobx/entities/user/User";
import { useStore } from "~/mobx/utils/useStore";
import { axiosErrorSchema } from "~/schemas";
import { styleConstantsV2 } from "~/style/styleConstantsV2";
import { isProblematicBrowser } from "~/utils/isProblematicBrowser";
import { bookingDataLayerPush } from "~/utils/logBookingEvent";
import { shadow } from "~/utils/shadow";
import { useStandaloneBookingFlow } from "../book-appointment/useStandaloneBookingFlow";
import type { AxiosError } from "axios";
import type * as AuthSession from "expo-auth-session";

const errorSchema = z.object({
  status: z.number(),
  data: z.object({
    statusCode: z.number().optional(),
  }),
});

const S = StyleSheet.create({
  container: {
    backgroundColor: "#FBFCFC",
    borderRadius: styleConstantsV2.borderRadius8,
    width: "100%",

    ...shadow(2),
  },
  bankidImage: {
    width: 21,
    height: 21,
  },
  wrapper: {
    position: "absolute",
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    justifyContent: "center",
    alignItems: "center",
  },
});

type ErrorType = "DUPLICATE_USER_FOUND" | "USER_NOT_FOUND";

interface BankIdLoginButtonProps {
  authRequest: AuthSession.AuthRequest;
  discovery: AuthSession.DiscoveryDocument;
  onLogin?: (user: UserInstance | undefined) => void;
  onError: (error: ErrorType) => void;
}

export const BankIdLoginButton = observer(function BankIdLoginButton(
  props: BankIdLoginButtonProps,
) {
  const isUnsupportedBrowser = isProblematicBrowser();
  const { isStandAloneBookingFlow } = useStandaloneBookingFlow();
  const store = useStore();

  const [isLoading, setIsLoading] = useState(false);

  const errorAlert = useErrorAlertV2();

  const login = useCallback(
    async function login() {
      setIsLoading(true);

      await store.authStore.login({
        discovery: props.discovery,
        authRequest: props.authRequest,
      });
    },
    [props.authRequest, props.discovery, store.authStore],
  );

  return (
    <>
      <Pressable
        disabled={isLoading}
        onPress={async () => {
          try {
            // If the browser is unsupported, show an error alert
            if (isUnsupportedBrowser) {
              errorAlert(
                new Error(
                  "Unsupported browser detected. Please use a different browser.",
                ),
                intl.formatMessage({
                  id: "pKBkbP",
                  defaultMessage: "Something went wrong!",
                  description: "Title for error message",
                }),
                intl.formatMessage({
                  id: "WHuzIz",
                  defaultMessage:
                    "You are using unsupported browser, please open this website in your default browser like Chrome or Safari.",
                  description:
                    "Description for error message when unsupported browser is detected",
                }),
                "Ok",
                undefined,
                false, // Show additional help
              );

              Sentry.captureException(
                new Error("Unsupported browser detected"),
              );

              return;
            }

            bookingDataLayerPush({
              event: "bankid_identify_start_web_app",
            });

            await login();

            let user: undefined | UserInstance;
            try {
              user = await store.authStore.getActiveUser();
            } catch (error: any) {
              console.log("ERR: ", error);

              if ("isAxiosError" in error) {
                const axiosError = error as AxiosError;

                const responseRaw = axiosError.response;

                const responseParsed = errorSchema.safeParse(responseRaw);

                if (responseParsed.success) {
                  const dataStatusCode = responseParsed.data.data.statusCode;
                  const status = responseParsed.data.status;

                  const statusCode = dataStatusCode || status;

                  if (statusCode === 404) {
                    // Log the error and continue with the login without user (new user flow)
                    console.log("🔑 User not found (404)");

                    // For APP we don't want to continue if the user is not found
                    if (isStandAloneBookingFlow === false) {
                      props.onError("USER_NOT_FOUND");
                      return;
                    }

                    // only continue if the user is not found in the standalone booking flow
                  } else if (statusCode === 409) {
                    console.log("Duplicate user found (409)");

                    props.onError("DUPLICATE_USER_FOUND");
                    return;
                  } else {
                    // Re-throw the error if it's not a 404
                    throw error;
                  }
                } else {
                  // if we cant parse the error we re-throw it
                  throw error;
                }
              }
            }

            // If login was successful, call the onLogin callback if it exists
            // it currently only exists for standalone booking flow
            // for the app navigation is handled by the _layout.tsx file
            props.onLogin?.(user);
          } catch (error) {
            const errorData = axiosErrorSchema.safeParse(error);

            const errorCode = errorData.success
              ? errorData.data.response.data.error.code
              : error instanceof Error
                ? error.message
                : "unknown";

            // Don't log the error if the user dismissed the login
            if (
              errorCode === "Login was dismissed" ||
              errorCode === "Login was cancelled"
            ) {
              return;
            }

            console.log("🔑 Error Code: ", errorCode);

            // Log the event to the booking data layer
            bookingDataLayerPush({
              event: "bankid_identify_failed_web_app",
              error_code: errorCode,
            });

            console.log("🔑 Errorr: ", JSON.stringify(error, null, 2));
            Sentry.captureException(error);
            errorAlert(error);
          } finally {
            setIsLoading(false);
          }
        }}
        padding16
        paddingHorizontal40
        centerContent
        flexDirectionRow
        style={({ pressed }) => [
          S.container,
          {
            opacity: pressed ? 0.95 : 1,
          },
        ]}
      >
        {isLoading && (
          <View style={S.wrapper}>
            <SpinnerV2 />
          </View>
        )}
        <Image source={require("./assets/bankid.png")} style={S.bankidImage} />
        <Spacer medium />

        <BodyText sizeLarge colorPrimary weightBold alignCenter>
          <FormattedMessage
            id="beujhJ"
            defaultMessage="Mobile BankID"
            description="Text for BankID login button"
          />
        </BodyText>
      </Pressable>

      {isLoading && (
        <Modal blockHardwareBackButton>
          <Pressable>
            <View flex />
          </Pressable>
        </Modal>
      )}
    </>
  );
});
